import { useTranslation } from "react-i18next";
import { KeyboardEvent, MouseEvent, useMemo } from "react";
import { SwitchLabel, SwitchLine, ToggleContainer } from "./style";

interface IToggleProps {
  isOn: boolean;
  /**
   * Typed as a mouse handler because every caller's handler is one. A keyboard
   * toggle passes its key event, which has the members callers use
   * (stopPropagation, preventDefault).
   */
  onToggle: (event: MouseEvent<HTMLElement>) => void;
  positiveText?: string;
  negativeText?: string;
  /** Not interactive; announced as disabled. */
  disabled?: boolean;
  /** Accessible name. Without it the switch is named by its On/Off text. */
  ariaLabel?: string;
}

/**
 * The shared on/off switch. It is a `div` for its layout, so it carries the
 * switch semantics itself: role="switch", aria-checked, focusable, and
 * Space/Enter toggle it, as a native control would.
 */
function Toggle({
  isOn,
  onToggle,
  positiveText,
  negativeText,
  disabled = false,
  ariaLabel,
}: IToggleProps) {
  const { t } = useTranslation("app", { keyPrefix: "shared.Toggle" });

  const switchLabel = useMemo(() => {
    const onText = positiveText ? t(positiveText) : t("on");
    const offText = negativeText ? t(negativeText) : t("off");
    return isOn ? onText : offText;
  }, [isOn, negativeText, positiveText, t]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      onToggle(event as unknown as MouseEvent<HTMLElement>);
    }
  };

  return (
    <ToggleContainer
      $isOn={isOn}
      $disabled={disabled}
      role="switch"
      aria-checked={isOn}
      aria-disabled={disabled || undefined}
      aria-label={ariaLabel}
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onToggle}
      onKeyDown={onKeyDown}
    >
      <div>
        <SwitchLine $isOn={isOn} />
        <SwitchLabel $isOn={isOn} aria-hidden={ariaLabel ? true : undefined}>
          {switchLabel}
        </SwitchLabel>
      </div>
    </ToggleContainer>
  );
}

export default Toggle;
